import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { getIndustry } from '@shared/catalog'
import { api, ApiError } from '../lib/api'
import { Banner, Button, Card, TextField } from '../ui'
import { Brand } from '../components/Layout'

function AuthShell({ title, subtitle, children, footer }: {
  title: string
  subtitle: string
  children: React.ReactNode
  footer: React.ReactNode
}) {
  return (
    // Sign-up and sign-in are still the marketing funnel, so they carry the editorial theme
    // rather than switching to the app palette a step early.
    <div className="editorial stack" style={{ minHeight: '100dvh' }}>
      <header className="container" style={{ paddingBlock: '24px' }}>
        <Brand />
      </header>
      <main className="grow row center" style={{ padding: '24px', alignItems: 'flex-start' }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>
          <Card variant="outlined">
            <div className="stack gap-5">
              <div className="stack gap-2">
                <h1 className="headline-small">{title}</h1>
                <p className="body-medium text-muted">{subtitle}</p>
              </div>
              {children}
            </div>
          </Card>
          <p className="body-medium text-muted" style={{ textAlign: 'center', marginTop: '20px' }}>
            {footer}
          </p>
        </div>
      </main>
    </div>
  )
}

export function Signup() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = useSearch({ strict: false }) as { industry?: string }
  const industryName = getIndustry(search.industry ?? '')?.name

  const [form, setForm] = useState({ name: '', company: '', email: '', password: '' })
  const [stage, setStage] = useState<'details' | 'code'>('details')
  const [code, setCode] = useState('')

  // Asked before choosing a form, so nobody is shown an emailed-code sign-up on a deployment that
  // cannot send one. While it is loading neither form renders: showing the password field for a
  // beat and then swapping it out reads as the page changing its mind.
  const methods = useQuery({ queryKey: ['auth-methods'], queryFn: api.authMethods, retry: false, staleTime: 60_000 })
  const emailCode = methods.data?.emailCode === true

  const enter = async () => {
    await queryClient.invalidateQueries({ queryKey: ['session'] })
    navigate({ to: '/onboarding', search: search.industry ? { industry: search.industry } : {} })
  }

  const mutation = useMutation({ mutationFn: () => api.signup(form), onSuccess: enter })

  const requestCode = useMutation({
    mutationFn: () => api.requestSignupCode({ name: form.name, company: form.company, email: form.email }),
    onSuccess: () => {
      setCode('')
      setStage('code')
    },
  })
  const verify = useMutation({
    mutationFn: () => api.verifySignup({ name: form.name, company: form.company, email: form.email, code }),
    onSuccess: enter,
  })

  const activeError = (emailCode ? (stage === 'code' ? (verify.error ?? requestCode.error) : requestCode.error) : mutation.error) as
    | Error
    | null

  const fieldError = (name: string) => {
    if (!(activeError instanceof ApiError)) return undefined
    const issues = activeError.issues as { fieldErrors?: Record<string, string[]> } | undefined
    return issues?.fieldErrors?.[name]?.[0]
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!emailCode) return mutation.mutate()
    if (stage === 'details') return requestCode.mutate()
    verify.mutate()
  }

  const busy = mutation.isPending || requestCode.isPending || verify.isPending

  return (
    <AuthShell
      title="Start your free trial"
      subtitle={
        emailCode && stage === 'code'
          ? `Enter the six-digit code we sent to ${form.email}.`
          : industryName
            ? `We will load the ${industryName} configuration once you are in. 14 days free, no card required.`
            : '14 days free. No card required. No WhatsApp Business API approval, no template queue.'
      }
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      {methods.isPending ? (
        <div style={{ height: '220px' }} aria-busy="true" />
      ) : (
        <form className="stack gap-4" onSubmit={submit} noValidate>
          {activeError && !fieldError('email') && <Banner tone="error">{activeError.message}</Banner>}
          <TextField
            label="Your name"
            autoComplete="name"
            required
            disabled={emailCode && stage === 'code'}
            value={form.name}
            error={fieldError('name')}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Company"
            autoComplete="organization"
            required
            disabled={emailCode && stage === 'code'}
            help="Becomes your workspace name. You can change it later."
            value={form.company}
            error={fieldError('company')}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
          {/* Locked once a code is out: changing the address without asking again would verify the
              new address against the old address's code, which can only fail. */}
          <TextField
            label="Work email"
            type="email"
            autoComplete="email"
            required
            disabled={emailCode && stage === 'code'}
            value={form.email}
            error={fieldError('email')}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          {!emailCode && (
            <TextField
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              help="At least 8 characters."
              value={form.password}
              error={fieldError('password')}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          )}
          {emailCode && stage === 'code' && (
            <TextField
              label="Six-digit code"
              // `one-time-code` lets iOS and Android offer the code from the notification.
              autoComplete="one-time-code"
              inputMode="numeric"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          )}
          <Button type="submit" full size="large" disabled={busy}>
            {verify.isPending || mutation.isPending
              ? 'Creating your workspace…'
              : requestCode.isPending
                ? 'Sending…'
                : emailCode
                  ? stage === 'details'
                    ? 'Email me a code'
                    : 'Create workspace'
                  : 'Create workspace'}
          </Button>
          {emailCode && stage === 'code' && (
            <div className="row center gap-3 wrap">
              <button type="button" className="btn btn-text" disabled={busy} onClick={() => requestCode.mutate()}>
                Resend code
              </button>
              <button
                type="button"
                className="btn btn-text"
                onClick={() => {
                  setStage('details')
                  setCode('')
                  verify.reset()
                  requestCode.reset()
                }}
              >
                Use a different email
              </button>
            </div>
          )}
        </form>
      )}
    </AuthShell>
  )
}

/**
 * Sign-in, two ways.
 *
 * The emailed code is the only way in wherever the deployment can send one: it is one field on a
 * phone, there is no password to forget, and receiving it proves control of the mailbox. There is
 * no password option beside it — anyone with an account has an email to send the code to.
 *
 * Where no mail transport exists the page asks first (`/api/auth/methods`) and shows the password
 * form instead, because with nothing able to deliver a code, removing the password would lock
 * everyone out. If that answer is missing or wrong, the code request's 503 switches to the
 * password form rather than leaving someone staring at a code that is never going to arrive.
 */
export function Login() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = useSearch({ strict: false }) as { ssoError?: string }

  const methods = useQuery({ queryKey: ['auth-methods'], queryFn: api.authMethods, retry: false, staleTime: 60_000 })
  const [codeFailed, setCodeFailed] = useState(false)
  // Unknown while loading counts as available, so the common case never flashes a password form.
  const method: 'code' | 'password' = codeFailed || methods.data?.emailCode === false ? 'password' : 'code'
  const [stage, setStage] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const enter = async () => {
    const session = await queryClient.fetchQuery({ queryKey: ['session'], queryFn: api.session })
    navigate({ to: session.workspace?.onboardingCompletedAt ? '/app' : '/onboarding' })
  }

  const passwordLogin = useMutation({
    mutationFn: () => api.login({ email, password }),
    onSuccess: enter,
  })

  const requestCode = useMutation({
    mutationFn: () => api.requestOtp({ email }),
    onSuccess: () => {
      setCode('')
      setStage('code')
      setNotice(null)
    },
    onError: (error) => {
      // No mail configured anywhere — codes are not a real option on this deployment, so stop
      // offering them instead of failing the same way every retry.
      if (error instanceof ApiError && error.status === 503) {
        setCodeFailed(true)
        setNotice('Email sign-in is not set up here yet — use your password.')
      }
    },
  })

  const verifyCode = useMutation({
    mutationFn: () => api.verifyOtp({ email, code }),
    onSuccess: enter,
  })


  /**
   * The workspace turned SSO on, so this account has no local way in. Both credential paths
   * answer 403 with the slug, and both are worth catching — someone whose password stopped
   * working is going to try the code next, and hitting the same wall twice with no way forward
   * is how a person concludes the product is broken rather than that they are on the wrong door.
   */
  const ssoRedirect = [passwordLogin.error, verifyCode.error, requestCode.error]
    .map((error) => (error instanceof ApiError && error.status === 403 ? error.body?.ssoWorkspaceSlug : null))
    .find((slug): slug is string => typeof slug === 'string' && slug.length > 0)
  /**
   * On the code step both mutations can fail, and only `verifyCode` was being read — so a
   * "Resend code" refused for hitting the per-address limit showed nothing at all. The button
   * greyed out, came back, and no code ever arrived: silence for exactly the person whose first
   * code went missing.
   */
  const activeError = (
    method === 'password'
      ? passwordLogin.error
      : stage === 'email'
        ? requestCode.error
        : (verifyCode.error ?? requestCode.error)
  ) as Error | null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (method === 'password') return passwordLogin.mutate()
    if (stage === 'email') return requestCode.mutate()
    verifyCode.mutate()
  }

  const busy = passwordLogin.isPending || requestCode.isPending || verifyCode.isPending

  return (
    <AuthShell
      title="Sign in"
      subtitle={
        method === 'code' && stage === 'code'
          ? `Enter the six-digit code we sent to ${email}.`
          : method === 'code'
            ? 'We will email you a one-time code — no password needed.'
            : 'Welcome back.'
      }
      footer={
        <>
          No account yet? <Link to="/signup">Start a free trial</Link>
        </>
      }
    >
      <form className="stack gap-4" onSubmit={submit} noValidate>
        {search.ssoError && <Banner tone="error">{search.ssoError}</Banner>}
        {notice && <Banner tone="info">{notice}</Banner>}
        {activeError && <Banner tone="error">{activeError.message}</Banner>}

        {/* Locked once a code is out: changing the address here without re-requesting would
            silently verify the new address against the old address's code and always fail. */}
        <TextField
          label="Work email"
          type="email"
          autoComplete="email"
          required
          disabled={method === 'code' && stage === 'code'}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        {method === 'password' && (
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}

        {method === 'code' && stage === 'code' && (
          <TextField
            label="Six-digit code"
            // `one-time-code` is what lets iOS and Android offer the code straight from the
            // notification; `numeric` gets the number pad instead of a full keyboard.
            autoComplete="one-time-code"
            inputMode="numeric"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        )}

        {ssoRedirect ? (
          // A plain anchor, not a Link: `/api/auth/sso/:slug/start` is a server redirect out to
          // the identity provider, and the client router would try to match it as a page.
          <a className="btn btn-filled btn-large" style={{ width: '100%', justifyContent: 'center' }} href={`/api/auth/sso/${ssoRedirect}/start`}>
            Continue with single sign-on
          </a>
        ) : (
        <Button type="submit" full size="large" disabled={busy}>
          {passwordLogin.isPending || verifyCode.isPending
            ? 'Signing in…'
            : requestCode.isPending
              ? 'Sending…'
              : method === 'password'
                ? 'Sign in'
                : stage === 'email'
                  ? 'Email me a code'
                  : 'Sign in'}
        </Button>
        )}

        {/* A resend that works looked exactly like a resend that was refused: both left the
            screen unchanged. */}
        {method === 'code' && stage === 'code' && requestCode.isSuccess && !requestCode.isPending && (
          <Banner tone="success">A new code is on its way to {email}.</Banner>
        )}

        <div className="row center gap-3 wrap">
          {method === 'code' && stage === 'code' && (
            <>
              <button
                type="button"
                className="btn btn-text"
                disabled={busy}
                onClick={() => requestCode.mutate()}
              >
                Resend code
              </button>
              <button
                type="button"
                className="btn btn-text"
                onClick={() => {
                  setStage('email')
                  setCode('')
                  verifyCode.reset()
                }}
              >
                Use a different email
              </button>
            </>
          )}

        </div>
      </form>
    </AuthShell>
  )
}
